import { getCurrentStorageUser } from '../common/localStorage/currentStorageUser'
import { reportCaughtError } from '@features/_common/errorHandler'
import {
    blobHasStrippableFat,
    extractFatSession,
    flushDebouncedPiniaPersists,
    questionIsFat,
    readPiniaBlob,
    stripFatFromBlob,
    writePiniaBlob,
} from './persist'
import { reloadDebug } from './reloadDebug'
import { viewTypes } from '@features/ViewState/types/viewTypes'

const DB_NAME = 'etrainer-test-session'
const STORE = 'sessions'
const SAVE_DELAY_MS = 1500
const BOOTSTRAP_TIMEOUT_MS = 2000

let pendingPatch = null
let pendingTimer = null
let revision = 0
let chain = Promise.resolve()

const enqueue = (task) => {
    const run = chain.then(task, task)
    chain = run.then(() => undefined, () => undefined)
    return run
}

const summarizeAnswers = (list) => {
    if (!Array.isArray(list)) return []
    return list.map((answer) => ({
        id: answer?.question?.id ?? null,
        ignored: Boolean(answer?.ignored),
        userInput: Array.isArray(answer?.userInput)
            ? answer.userInput.map((item) => item && item.value)
            : [],
    }))
}

const indexedDbAvailable = () => typeof indexedDB !== 'undefined'

// IndexedDB structured-clone rejects Vue reactive proxies (DataCloneError).
export const toPlain = (value) => JSON.parse(JSON.stringify(value))

const openDb = () => {
    if (!indexedDbAvailable()) return Promise.resolve(null)
    return new Promise((resolve) => {
        let request
        try {
            request = indexedDB.open(DB_NAME, 1)
        } catch (e) {
            reportCaughtError(e, 'test-session.open')
            resolve(null)
            return
        }
        request.onupgradeneeded = () => {
            const db = request.result
            if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
        }
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => {
            reportCaughtError(request.error, 'test-session.open-failed')
            resolve(null)
        }
        request.onblocked = () => {
            reportCaughtError(new Error('IndexedDB open blocked'), 'test-session.open-blocked')
        }
    })
}

export const loadTestSession = async (key = getCurrentStorageUser()) => {
    const db = await openDb()
    if (!db) return null
    return new Promise((resolve) => {
        try {
            const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(key)
            request.onsuccess = () => {
                db.close()
                resolve(request.result || null)
            }
            request.onerror = () => {
                reportCaughtError(request.error, 'test-session.load-failed')
                db.close()
                resolve(null)
            }
        } catch (e) {
            reportCaughtError(e, 'test-session.load')
            try { db.close() } catch (closeError) { /* ignore */ }
            resolve(null)
        }
    })
}

export const saveTestSession = async (key, session) => {
    let plain
    try {
        plain = toPlain(session)
    } catch (e) {
        reportCaughtError(e, 'test-session.clone')
        return false
    }
    const db = await openDb()
    if (!db) return false
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(STORE, 'readwrite')
            tx.objectStore(STORE).put(plain, key)
            tx.oncomplete = () => {
                db.close()
                resolve(true)
            }
            tx.onerror = () => {
                reportCaughtError(tx.error, 'test-session.store-failed')
                db.close()
                resolve(false)
            }
        } catch (e) {
            reportCaughtError(e, 'test-session.store')
            try { db.close() } catch (closeError) { /* ignore */ }
            resolve(false)
        }
    })
}

const deleteTestSession = async (key) => {
    const db = await openDb()
    if (!db) return
    await new Promise((resolve) => {
        try {
            const tx = db.transaction(STORE, 'readwrite')
            tx.objectStore(STORE).delete(key)
            tx.oncomplete = () => {
                db.close()
                resolve()
            }
            tx.onerror = () => {
                reportCaughtError(tx.error, 'test-session.delete-failed')
                db.close()
                resolve()
            }
        } catch (e) {
            reportCaughtError(e, 'test-session.delete')
            try { db.close() } catch (closeError) { /* ignore */ }
            resolve()
        }
    })
}

export const scheduleTestSessionPatch = (patch, { immediate = false, delay = SAVE_DELAY_MS } = {}) => {
    pendingPatch = { ...(pendingPatch || {}), ...patch }
    if (immediate) return flushTestSessionSave({ reason: 'immediate' })
    if (pendingTimer) clearTimeout(pendingTimer)
    pendingTimer = setTimeout(() => {
        pendingTimer = null
        flushTestSessionSave({ reason: 'debounce' })
    }, delay)
    return Promise.resolve()
}

export const flushTestSessionSave = ({ reason = 'flush' } = {}) => {
    flushDebouncedPiniaPersists()
    if (pendingTimer) clearTimeout(pendingTimer)
    pendingTimer = null
    if (!pendingPatch) {
        if (reason === 'immediate') {
            console.warn('[test-session] immediate save skipped, nothing pending')
        }
        return Promise.resolve()
    }
    const patch = pendingPatch
    pendingPatch = null
    const questionCount = Array.isArray(patch.allTestQuestions) ? patch.allTestQuestions.length : undefined
    if (!indexedDbAvailable()) {
        console.warn('[test-session] save skipped, indexedDB is not available', { reason, questionCount })
        return Promise.resolve()
    }
    const rev = ++revision
    const key = getCurrentStorageUser()
    console.warn('[test-session] saving', { reason, key, questionCount, rev })
    return enqueue(async () => {
        if (revision !== rev) {
            console.warn('[test-session] save skipped, a newer clear replaced it', { rev, revision })
            return
        }
        try {
            const existing = (await loadTestSession(key)) || {}
            if (revision !== rev) {
                console.warn('[test-session] save aborted after read, revision changed', { rev, revision })
                return
            }
            const payload = { ...existing, ...patch, savedAt: Date.now() }
            const saved = await saveTestSession(key, payload)
            if (saved) {
                const storedSession = await loadTestSession(key)
                const answers = summarizeAnswers(storedSession?.userAnswers)
                console.warn('[test-session] stored', {
                    key,
                    questionCount: Array.isArray(payload.allTestQuestions) ? payload.allTestQuestions.length : 0,
                    bytes: JSON.stringify(payload).length,
                    answerCount: answers.length,
                    answers,
                })
            } else {
                console.warn('[test-session] store returned false', { key, questionCount })
            }
        } catch (e) {
            reportCaughtError(e, 'test-session.save')
        }
    })
}

export const clearTestSession = () => {
    const rev = ++revision
    pendingPatch = null
    if (pendingTimer) clearTimeout(pendingTimer)
    pendingTimer = null
    if (!indexedDbAvailable()) {
        console.warn('[test-session] clear skipped, indexedDB is not available')
        return Promise.resolve()
    }
    const key = getCurrentStorageUser()
    console.warn('[test-session] clearing', { key, rev })
    return enqueue(async () => {
        if (revision !== rev) {
            console.warn('[test-session] clear skipped, a newer save exists', { rev, revision })
            return
        }
        try {
            await deleteTestSession(key)
            console.warn('[test-session] cleared', { key })
        } catch (e) {
            reportCaughtError(e, 'test-session.clear')
        }
    })
}

const answersLookSlim = (list) =>
    !Array.isArray(list) || list.length === 0 || list.every((answer) => !questionIsFat(answer?.question))

export const mergeSessionAnswers = (memory, stored) => {
    if (!Array.isArray(stored) || stored.length === 0) return memory || []
    if (!Array.isArray(memory) || memory.length === 0) return stored
    const byId = new Map(stored.map((answer) => [answer?.question?.id, answer]))
    const seen = new Set()
    const merged = memory.map((answer) => {
        const id = answer?.question?.id
        seen.add(id)
        const rich = byId.get(id)
        if (!rich?.question) return answer
        return { ...rich, ...answer, question: rich.question }
    })
    stored.forEach((answer) => {
        const id = answer?.question?.id
        if (!seen.has(id)) merged.push(answer)
    })
    return merged
}

const questionsById = (tree) => {
    const byId = new Map()
    if (!Array.isArray(tree)) return byId
    tree.forEach((main) => {
        const subs = Array.isArray(main?.subCategories) ? main.subCategories : []
        subs.forEach((sub) => {
            const questions = Array.isArray(sub?.questions) ? sub.questions : []
            questions.forEach((question) => {
                if (!question || question.id == null) return
                byId.set(question.id, question)
                byId.set(String(question.id), question)
            })
        })
    })
    return byId
}

// localStorage keeps only {id, marked}. Scoring and "Weiter" need the full question from the test tree.
const enrichSlimAnswers = (answers, tree) => {
    const byId = questionsById(tree)
    if (!Array.isArray(answers) || byId.size === 0) return answers
    return answers.map((answer) => {
        if (!answer || questionIsFat(answer.question)) return answer
        const full = byId.get(answer.question?.id) || byId.get(String(answer.question?.id))
        if (!full) return answer
        return { ...answer, question: full }
    })
}

const onRealTestRoute = () => {
    if (typeof window === 'undefined') return false
    return /#\/profession\/[^/]+\/test(?:[/?#]|$)/.test(window.location.hash || '')
}

const restoreInterruptedRealTest = async () => {
    if (!onRealTestRoute()) return
    const { useViewStateStore } = await import('./stores/viewState')
    const { useTestTemplateStore } = await import('./stores/testTemplate')
    const view = useViewStateStore()
    const template = useTestTemplateStore()
    const tree = template.allTestQuestions
    if (!Array.isArray(tree) || tree.length === 0) return
    const question = template.currentTestQuestion
    if (!question || question.id == null) return
    if (view.isTestOver || view.inResultsMode) return
    if (view.currentView && view.currentView !== viewTypes.none) return

    view.startTest({ isRealTest: true })
    view.changeViewTypeTo(viewTypes.questionPresenter)
}

const applySession = async (session) => {
    if (!session) return
    const { useTestTemplateStore } = await import('./stores/testTemplate')
    const { useAnswersStore } = await import('./stores/answers')
    const { useMainCategoriesStore } = await import('./stores/mainCategories')
    const { useSubCategoriesStore } = await import('./stores/subCategories')

    const template = useTestTemplateStore()
    const answers = useAnswersStore()
    const mains = useMainCategoriesStore()
    const subs = useSubCategoriesStore()

    if (template.allTestQuestions.length === 0 && Array.isArray(session.allTestQuestions) && session.allTestQuestions.length > 0) {
        template.hydrateAllTestQuestions(session.allTestQuestions)
    }
    if (Array.isArray(session.userAnswers) && session.userAnswers.length > 0 && answersLookSlim(answers.userAnswers)) {
        answers.hydrateUserAnswers(mergeSessionAnswers(answers.userAnswers, session.userAnswers))
    }
    if (mains.allMainCategories.length === 0 && Array.isArray(session.allMainCategories) && session.allMainCategories.length > 0) {
        mains.hydrateCatalog(session)
    }
    if (subs.allLoadedSubCategories.length === 0 && Array.isArray(session.allLoadedSubCategories) && session.allLoadedSubCategories.length > 0) {
        subs.hydrateCatalog(session)
    }
    if (answers.userAnswers.length > 0 && template.allTestQuestions.length > 0) {
        answers.hydrateUserAnswers(enrichSlimAnswers(answers.userAnswers, template.allTestQuestions))
    }
}

const bootstrapTestSessionInner = async () => {
    const blob = readPiniaBlob()
    const fromLocal = extractFatSession(blob)
    const key = getCurrentStorageUser()
    let session = await loadTestSession(key)
    if (!session && fromLocal && indexedDbAvailable()) {
        const payload = { ...fromLocal, savedAt: Date.now() }
        const saved = await saveTestSession(key, payload)
        if (saved) session = payload
    }
    if (session && blobHasStrippableFat(blob)) {
        writePiniaBlob(stripFatFromBlob(blob))
    }
    await applySession(session)
    await restoreInterruptedRealTest()
    return {
        sessionFound: Boolean(session),
        savedQuestionCount: Array.isArray(session?.allTestQuestions) ? session.allTestQuestions.length : 0,
    }
}

export const bootstrapTestSession = () => {
    let settled = false
    let timer
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => {
            if (!settled) {
                reloadDebug('bootstrap', { timedOut: true })
                reportCaughtError(new Error('Test session bootstrap timed out'), 'test-session.bootstrap-timeout')
            }
            resolve()
        }, BOOTSTRAP_TIMEOUT_MS)
    })
    const load = bootstrapTestSessionInner()
        .then((info) => {
            settled = true
            reloadDebug('bootstrap', { timedOut: false, ...(info || {}) })
        })
        .finally(() => clearTimeout(timer))
    return Promise.race([load, timeout]).catch((e) => {
        reportCaughtError(e, 'test-session.bootstrap')
        reloadDebug('bootstrap-failed', { message: e && e.message ? e.message : String(e) })
    })
}

export const readTestSessionSummary = async () => {
    const key = getCurrentStorageUser()
    const session = await loadTestSession(key)
    const answers = summarizeAnswers(session?.userAnswers)
    const summary = { key, count: answers.length, answers }
    console.warn('[test-session] read', summary)
    return summary
}

if (typeof window !== 'undefined') {
    window.readTestSession = readTestSessionSummary
    window.addEventListener('pagehide', () => {
        flushTestSessionSave()
    })
}
